"use dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./editor-styles.css";

type EditorProps = Readonly<{
  content?: string | null;
  onContentChange?: (html: string) => void;
  primaryColor?: string;
  textColor?: string;
  toolbarBg?: string;
  borderColor?: string;
  containerBg?: string;
  placeHolder?: string;
  editorHeight?: number;
  dom?: import("expo/dom").DOMProps;
}>;

export default function DOMQuillEditor({
  content,
  onContentChange,
  primaryColor = "#0669B3",
  textColor = "#22272a",
  toolbarBg = "#607da1",
  borderColor = "#DFE5EC",
  containerBg = "#FFFFFF",
  placeHolder = "#526581",
  editorHeight = 320,
}: EditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<any>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { t } = useTranslation();

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--editor-primary", primaryColor);
    root.style.setProperty("--editor-text", textColor);
    root.style.setProperty("--editor-toolbar-bg", toolbarBg);
    root.style.setProperty("--editor-border", borderColor);
    root.style.setProperty("--editor-container-bg", containerBg);
    root.style.setProperty("--color-slate-default", placeHolder);
  }, [
    primaryColor,
    textColor,
    toolbarBg,
    borderColor,
    containerBg,
    placeHolder,
  ]);

  useEffect(() => {
    const initQuill = async () => {
      const { default: Quill } = await import("quill");
      await import("quill/dist/quill.snow.css");

      if (!editorRef.current) return;

      const quill = new Quill(editorRef.current, {
        theme: "snow",
        modules: {
          toolbar: [
            ["bold", "italic"],
            [{ header: 1 }, { header: 2 }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ indent: "-1" }, { indent: "+1" }],
            [{ align: [false, "center", "right"] }],
          ],
        },
        placeholder: t("LABEL_START_TYPING"),
      });

      quillRef.current = quill;

      if (content) {
        quill.clipboard.dangerouslyPasteHTML(content);
      }

      quill.on("text-change", () => {
        onContentChange?.(quill.root.innerHTML);
      });

      quill.on("selection-change", (range: any) => {
        if (!wrapperRef.current) return;
        if (range) {
          wrapperRef.current.classList.add("ql-wrapper--focused");
        } else {
          wrapperRef.current.classList.remove("ql-wrapper--focused");
        }
      });

      setIsLoading(false);
    };

    initQuill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} className="ql-wrapper" style={{ height: editorHeight }}>
      {isLoading && (
        <div className="ql-loading-overlay">
          <div className="ql-spinner" />
          <span className="ql-loading-text">{t("LABEL_LOADING_EDITOR")}</span>
        </div>
      )}
      <div
        ref={editorRef}
        style={{ flex: 1, minHeight: 0, visibility: isLoading ? "hidden" : "visible" }}
      />
    </div>
  );
}
